<template>
  <div class="notifications-settings" :class="'type-' + type">
    <h4 class="notifications-settings__title" :class="{ collapsed: !expanded, expanded }" @click="toggleExpanded">
      {{ title }}
      <span class="notifications-settings__arrow" :class="{ expanded }">▶</span>
    </h4>

    <template v-if="expanded">
      <div class="settings-body" :class="{ 'enabled-false': !isEnabled }">
      <template v-if="type === 'web'">
        <div class="setting-item">
          <label>
            <input
              type="checkbox"
              v-model="notificationsEnabled"
              @change="updateNotificationsSettings"
            />
            Включить уведомление нарушений
          </label>
        </div>
      </template>

      <template v-if="type === 'messenger' || type === 'admin'">
        <template v-if="messengerDataSource || type === 'admin'">
          <div class="setting-item">
            <label>
              <input
                type="checkbox"
                v-model="messengerEnabled"
                @change="onMessengerEnabledChange"
              />
              Включить уведомление нарушений в MAX мессенджер
            </label>
          </div>
          <template v-if="messengerEnabled">
            <div class="setting-item">
              <label class="setting-label">Название канала:</label>
              <input
                type="text"
                :value="messengerDataSource.name"
                disabled
                class="form-control"
              />
            </div>
            <div class="setting-item">
              <label class="setting-label">ID канала:</label>
              <input
                type="text"
                v-model="messengerChannelId"
                placeholder="Например: -123456789"
                class="form-control"
                @change="onMessengerFieldChange"
              />
            </div>
          </template>
        </template>
        <div v-else class="no-channel-message">
          У вас нет закрытого канала для отправки уведомлений
        </div>
      </template>

      <div class="objects-list" v-if="showObjects">
      <h5>Объекты для уведомлений:</h5>

      <div v-if="type === 'messenger' || type === 'admin'" class="setting-item">
        <label>
          <input
            type="checkbox"
            v-model="messengerAllObjects"
            @change="onAllObjectsChange"
          />
          Все автомобили
        </label>
      </div>

      <div class="flex"
        v-if="Object.keys(globalObjectsList).length > 0">
        <div class="objects-left">
          <div class="objects-filter">
            <div class="search-container">
              <input
                type="text"
                v-model="objectsFilter"
                placeholder="Поиск по государственному номеру..."
                class="form-control search-input"
              />
              <div class="filter-buttons">
                <button @click="selectAllObjects" class="btn btn-secondary btn-square" title="Выбрать все объекты">
                  <span class="btn-text">✓</span>
                </button>
                <button @click="clearSelection" class="btn btn-secondary btn-square" title="Очистить выбор">
                  <span class="btn-text">✗</span>
                </button>
              </div>
            </div>
          </div>

          <div class="objects-container">
            <div
              v-for="id in filteredObjects"
              :key="id"
              class="object-item"
            >
              <label>
                <input
                  type="checkbox"
                  :disabled="isObjectDisabled"
                  :checked="isObjectSelected(id)"
                  @change="toggleObject(id)"
                />
                <span>
                  {{ globalObjectsList[id].state_number }} ({{ globalObjectsList[id].name }})
                </span>
              </label>
            </div>
          </div>
        </div>

        <div class="selected-objects-summary">
          <h6>Выбранные объекты ({{ selectedObjectsSummary.length }}):</h6>
          <div class="selected-objects-list">
            <div
              v-for="(object, index) in selectedObjectsSummary"
              :key="object.id"
              class="selected-object-tag"
            >
              {{ object.state_number }}
            </div>
          </div>
        </div>
      </div>
      <div
        v-else
        class=""
      >
        <preloader />
      </div>
    </div>

      <div class="violations-list" v-if="showViolations">
      <h5>Список нарушений:</h5>

      <div v-if="type === 'messenger' || type === 'admin'" class="setting-item violations-actions">
        <button
          @click="selectOnlyCritical"
          class="btn btn-secondary btn-sm"
          :class="{ 'btn-active': onlyCriticalActive }"
        >
          Только грубые нарушения
        </button>
        <button @click="selectAllVisibleViolations" class="btn btn-secondary btn-sm">
          Включить все видимые
        </button>
        <button @click="deselectAllViolations" class="btn btn-secondary btn-sm">
          Отключить все
        </button>
      </div>

      <div class="violations-container"
        v-if="violationsList.length > 0"
      >
        <div
          v-for="violation in violationsList"
          :key="violation.id"
          class="violation-item"
        >
          <label>
            <input
              type="checkbox"
              :checked="isViolationSelected(violation.id)"
              @change="toggleViolation(violation.id)"
            />
            <span :class="{ 'critical': violation.critical }">
              {{ violation.description }}
              <span v-if="violation.critical" class="critical-badge">(Критично)</span>
            </span>
          </label>
        </div>
      </div>
      <div
          v-else
          class=""
        >
          <preloader />
        </div>
    </div>
    </div>
    </template>
  </div>
</template>

<script setup>
import { inject, ref, watch, computed, onMounted } from 'vue';

import Preloader from '../Components/Preloader.vue';
import { accessToken } from '@/src/jwtSender';

const props = defineProps({
  type: {
    type: String,
    default: 'web',
    validator: (value) => ['web', 'messenger', 'admin'].includes(value),
  },
  modelValue: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['update:modelValue']);

const userData = inject('userData');
const globalObjectsList = inject('globalObjectsList');
const restartNotificationsInterval = inject('restartNotificationsInterval');
const loadAvailableViolations = inject('loadAvailableViolations');

const messengerDataSource = computed(() => {
  if (props.type === 'admin') return props.modelValue;
  if (props.type === 'messenger') return userData.messenger;
  return null;
});

const title = computed(() => {
  if (props.type === 'messenger') return 'Настройки уведомлений нарушений в MAX мессенджер';
  if (props.type === 'admin') return 'Настройки уведомлений нарушений в MAX мессенджер';
  return 'Настройки уведомлений нарушений WEB';
});

const isEnabled = computed(() => {
  if (props.type === 'web') return notificationsEnabled.value;
  return messengerEnabled.value;
});

const showObjects = computed(() => {
  if (props.type === 'messenger' || props.type === 'admin') return messengerEnabled.value;
  return notificationsEnabled.value;
});

const showViolations = computed(() => {
  if (props.type === 'messenger' || props.type === 'admin') return messengerEnabled.value;
  return notificationsEnabled.value;
});

const expanded = ref(false);

const toggleExpanded = () => {
  expanded.value = !expanded.value;
};

// ---- Web-specific state ----
const isFirstTimeGotSettingNotif = ref(false)
const notificationsEnabled = ref(false);

// ---- Messenger/Admin state ----
const isFirstTimeGotMessengerSetting = ref(false)
const onlyCriticalActive = ref(false);
const messengerEnabled = ref(false);
const messengerChannelId = ref('');
const messengerAllObjects = ref(false);
const messengerSelectedObjects = ref([]);
const messengerSelectedViolations = ref([]);

// ---- Shared state ----
const objectsFilter = ref('');

const filteredObjects = computed(() => {
  if (!objectsFilter.value) {
    return Object.keys(globalObjectsList);
  }

  const filter = objectsFilter.value.toLowerCase();
  const filtered = [];

  for (const id in globalObjectsList) {
    if (globalObjectsList.hasOwnProperty(id)) {
      const object = globalObjectsList[id];
      if (object.state_number && object.state_number.toLowerCase().includes(filter)) {
        filtered.push(id)
      }
    }
  }

  return filtered;
});

const selectedObjectsSummary = computed(() => {
  let source;
  if (props.type === 'messenger' || props.type === 'admin') {
    if (messengerAllObjects.value) {
      source = Object.keys(globalObjectsList);
    } else {
      source = messengerSelectedObjects.value;
    }
  } else {
    source = userData.user?.settings?.selectedObjects || [];
  }

  const summary = [];
  for (const id of source) {
    if (globalObjectsList[id]) {
      summary.push({
        id,
        state_number: globalObjectsList[id].state_number
      });
    }
  }
  return summary;
});

const isObjectDisabled = computed(() => {
  return (props.type === 'messenger' || props.type === 'admin') && messengerAllObjects.value;
});

const isObjectSelected = (id) => {
  if (props.type === 'messenger' || props.type === 'admin') {
    if (messengerAllObjects.value) return true;
    return messengerSelectedObjects.value.includes(id);
  }
  return userData.user?.settings?.selectedObjects?.includes(id);
};

const toggleObject = (id) => {
  if (props.type === 'messenger' || props.type === 'admin') {
    const idx = messengerSelectedObjects.value.indexOf(id);
    if (idx >= 0) {
      messengerSelectedObjects.value.splice(idx, 1);
    } else {
      messengerSelectedObjects.value.push(id);
    }
    onMessengerFieldChange();
  } else {
    if (!userData.user.settings.selectedObjects) {
      userData.user.settings.selectedObjects = [];
    }
    const idx = userData.user.settings.selectedObjects.indexOf(id);
    if (idx >= 0) {
      userData.user.settings.selectedObjects.splice(idx, 1);
    } else {
      userData.user.settings.selectedObjects.push(id);
    }
    updateObjectsSettings();
  }
};

const isViolationSelected = (id) => {
  if (props.type === 'messenger' || props.type === 'admin') {
    return messengerSelectedViolations.value.includes(id);
  }
  return userData.user?.settings?.selectedViolations?.includes(id);
};

const toggleViolation = (id) => {
  if (props.type === 'messenger' || props.type === 'admin') {
    const idx = messengerSelectedViolations.value.indexOf(id);
    if (idx >= 0) {
      messengerSelectedViolations.value.splice(idx, 1);
    } else {
      messengerSelectedViolations.value.push(id);
    }
    onMessengerFieldChange();
  } else {
    if (!userData.user.settings.selectedViolations) {
      userData.user.settings.selectedViolations = [];
    }
    const idx = userData.user.settings.selectedViolations.indexOf(id);
    if (idx >= 0) {
      userData.user.settings.selectedViolations.splice(idx, 1);
    } else {
      userData.user.settings.selectedViolations.push(id);
    }
    updateViolationsSettings();
  }
};

const violationsList = computed(() => {
  const violations = userData?.user?.notificationSettings?.availableViolations || [];
  if ((props.type === 'messenger' || props.type === 'admin') && onlyCriticalActive.value) {
    return violations.filter(v => v.critical);
  }
  return violations;
});

// ---- Web-specific functions ----
const initializeSettings = () => {
  if (isFirstTimeGotSettingNotif.value) {
    return
  }

  if (!userData.user || !userData.user.settings) {
    notificationsEnabled.value = false;
    if (loadAvailableViolations) {
      loadAvailableViolations()
    }
    return;
  }

  const settings = userData.user.settings;
  notificationsEnabled.value = settings.notificationsEnabled;

  if (!settings.selectedObjects) {
    settings.selectedObjects = Object.keys(globalObjectsList);
  }

  if (loadAvailableViolations) {
    loadAvailableViolations()
  }
  isFirstTimeGotSettingNotif.value = true
}

const updateNotificationsSettings = async () => {
  const settings = userData.user.settings || {};
  settings.notificationsEnabled = notificationsEnabled.value;
  if (loadAvailableViolations) {
    loadAvailableViolations()
  }
  restartNotificationsInterval();

  await saveUserSettings(settings);
};

const updateObjectsSettings = async (e) => {
  const settings = userData.user.settings || {};

  await saveUserSettings(settings);
};

const updateViolationsSettings = async (e) => {
  const settings = userData.user.settings || {};

  await saveUserSettings(settings);
};

const selectAllObjects = () => {
  if (props.type === 'messenger' || props.type === 'admin') {
    messengerAllObjects.value = true;
    messengerSelectedObjects.value = Object.keys(globalObjectsList);
    onMessengerFieldChange();
    return;
  }
  if (!userData.user.settings) {
    userData.user.settings = {};
  }
  if (!userData.user.settings.selectedObjects) {
    userData.user.settings.selectedObjects = [];
  }

  userData.user.settings.selectedObjects = Object.keys(globalObjectsList);
  updateObjectsSettings();
};

const clearSelection = () => {
  if (props.type === 'messenger' || props.type === 'admin') {
    messengerAllObjects.value = false;
    messengerSelectedObjects.value = [];
    onMessengerFieldChange();
    return;
  }
  if (userData.user.settings && userData.user.settings.selectedObjects) {
    userData.user.settings.selectedObjects = [];
    updateObjectsSettings();
  }
};

const onAllObjectsChange = () => {
  if (messengerAllObjects.value) {
    messengerSelectedObjects.value = Object.keys(globalObjectsList);
  } else {
    messengerSelectedObjects.value = [];
  }
  onMessengerFieldChange();
};

const selectOnlyCritical = () => {
  onlyCriticalActive.value = !onlyCriticalActive.value;
};

const selectAllVisibleViolations = () => {
  const ids = violationsList.value.map(v => v.id);
  if (props.type === 'messenger' || props.type === 'admin') {
    for (const id of ids) {
      if (!messengerSelectedViolations.value.includes(id)) {
        messengerSelectedViolations.value.push(id);
      }
    }
    onMessengerFieldChange();
  } else {
    if (!userData.user.settings.selectedViolations) {
      userData.user.settings.selectedViolations = [];
    }
    for (const id of ids) {
      if (!userData.user.settings.selectedViolations.includes(id)) {
        userData.user.settings.selectedViolations.push(id);
      }
    }
    updateViolationsSettings();
  }
};

const deselectAllViolations = () => {
  if (props.type === 'messenger' || props.type === 'admin') {
    messengerSelectedViolations.value = [];
    onMessengerFieldChange();
  } else {
    if (userData.user.settings) {
      userData.user.settings.selectedViolations = [];
    }
    updateViolationsSettings();
  }
};

const saveUserSettings = async (settings) => {
  try {
    const token = accessToken.value.token
    const response = await fetch(
      `${process.env.VUE_APP_BACKEND_URL || window.location.origin + '/'}Api/user/settings`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ settings }),
      }
    );

    if (response.ok) {
      userData.user.settings = settings;
      if (restartNotificationsInterval) {
        restartNotificationsInterval();
      }
    } else {
      console.error('Ошибка при обновлении настроек пользователя');
    }
  } catch (error) {
    console.error('Ошибка при сохранении настроек:', error);
  }
};

// ---- Messenger functions ----
const initMessenger = () => {
  if (isFirstTimeGotMessengerSetting.value) return;
  if (userData.messenger === undefined || userData.messenger === null) {
    return;
  }

  if (userData.messenger === false) {
    messengerEnabled.value = false;
    return;
  }

  messengerChannelId.value = userData.messenger.channelId || '';

  try {
    const settings = typeof userData.messenger.settings === 'string'
      ? JSON.parse(userData.messenger.settings)
      : (userData.messenger.settings || {});
    messengerEnabled.value = settings.notificationsEnabled ?? false;
    messengerSelectedViolations.value = settings.selectedViolations || [];

    const rawObjects = settings.selectedObjects || [];
    if (rawObjects[0] === '*') {
      messengerAllObjects.value = true;
      messengerSelectedObjects.value = Object.keys(globalObjectsList);
    } else {
      messengerAllObjects.value = false;
      messengerSelectedObjects.value = rawObjects;
    }
    isFirstTimeGotMessengerSetting.value = true;
  } catch {
    messengerEnabled.value = false;
    messengerAllObjects.value = false;
    messengerSelectedObjects.value = [];
    messengerSelectedViolations.value = [];
  }
};

const saveMessengerSettings = async () => {
  try {
    const token = accessToken.value.token

    const objectsToSave = messengerAllObjects.value
      ? ['*']
      : messengerSelectedObjects.value;

    userData.messenger.channelId = messengerChannelId.value;
    userData.messenger.settings = JSON.stringify({
      notificationsEnabled: messengerEnabled.value,
      selectedObjects: objectsToSave,
      selectedViolations: messengerSelectedViolations.value,
    });

    const response = await fetch(
      `${process.env.VUE_APP_BACKEND_URL || window.location.origin + '/'}Api/messenger/settings`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          channelId: messengerChannelId.value,
          settings: {
            notificationsEnabled: messengerEnabled.value,
            selectedObjects: objectsToSave,
            selectedViolations: messengerSelectedViolations.value,
          }
        }),
      }
    );

    if (!response.ok) {
      console.error('Ошибка при сохранении настроек мессенджера');
    }
  } catch (error) {
    console.error('Ошибка при сохранении настроек мессенджера:', error);
  }
};

const onMessengerEnabledChange = () => {
  onMessengerFieldChange();
};

const onMessengerFieldChange = () => {
  if (props.type === 'admin') {
    const objectsToSave = messengerAllObjects.value
      ? ['*']
      : messengerSelectedObjects.value;

    const updated = {
      ...props.modelValue,
      channelId: messengerChannelId.value,
      settings: JSON.stringify({
        notificationsEnabled: messengerEnabled.value,
        selectedObjects: objectsToSave,
        selectedViolations: messengerSelectedViolations.value,
      }),
    };
    emit('update:modelValue', updated);
  } else if (props.type === 'messenger') {
    saveMessengerSettings();
  }
};

// ---- Admin functions ----
const initAdmin = () => {
  if (!props.modelValue) {
    messengerEnabled.value = false;
    return;
  }

  messengerChannelId.value = props.modelValue.channelId || '';

  try {
    const settings = typeof props.modelValue.settings === 'string'
      ? JSON.parse(props.modelValue.settings)
      : (props.modelValue.settings || {});
    messengerEnabled.value = settings.notificationsEnabled ?? false;
    messengerSelectedViolations.value = settings.selectedViolations || [];

    const rawObjects = settings.selectedObjects || [];
    if (rawObjects[0] === '*') {
      messengerAllObjects.value = true;
      messengerSelectedObjects.value = Object.keys(globalObjectsList);
    } else {
      messengerAllObjects.value = false;
      messengerSelectedObjects.value = rawObjects;
    }
  } catch {
    messengerEnabled.value = false;
    messengerAllObjects.value = false;
    messengerSelectedObjects.value = [];
    messengerSelectedViolations.value = [];
  }
};

// ---- Lifecycle ----
onMounted(() => {
  if (props.type === 'web') {
    initializeSettings();
  } else if (props.type === 'admin') {
    initAdmin();
  } else {
    initMessenger();
  }
});

watch(() => props.type, (newType) => {
  if (newType === 'web') {
    initializeSettings();
  } else if (newType === 'admin') {
    initAdmin();
  } else {
    initMessenger();
  }
});

watch(() => props.modelValue, () => {
  if (props.type === 'admin') {
    initAdmin();
  }
}, { deep: true });

watch(() => userData, () => {
  if (props.type === 'web') {
    initializeSettings();
  } else if (props.type !== 'admin') {
    initMessenger();
  }
}, { deep: true });
</script>

<style lang="scss" scoped>
.notifications-settings {
  margin-top: 20px;
  padding: 15px;
  border-radius: 5px;
  background-color: #f9f9f9;
}

.type-web {
  outline: 3px solid #67b66b;
  outline-offset: -3px;
}

.type-messenger,
.type-admin {
  outline: 3px solid #423cea;
  outline-offset: -3px;
}

.notifications-settings__title {
  position: relative;
  margin-top: 0;
  margin-bottom: 15px;
  color: #333;
  cursor: pointer;
  user-select: none;
}

.notifications-settings__title.collapsed {
  margin-bottom: 0;
}

.notifications-settings__title.expanded::after {
  content: '';
  position: absolute;
  left: -15px;
  right: -15px;
  bottom: -16px;
  border-top: 2px solid #ddd;
}

.type-web .notifications-settings__title.expanded::after {
  border-color: black;
}

.type-messenger .notifications-settings__title.expanded::after,
.type-admin .notifications-settings__title.expanded::after {
  border-color: black;
}

.settings-body.enabled-false .setting-item {
  margin-bottom: 0;
}

.notifications-settings__arrow {
  display: inline-block;
  margin-right: 8px;
  font-size: 16px;
  transition: transform 0.2s;
}

.notifications-settings__arrow.expanded {
  transform: rotate(90deg);
}

.setting-item {
  margin-bottom: 15px;
  margin-top: 27px;
}

.setting-item label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-weight: 500;
}

.setting-item input[type="checkbox"] {
  margin-right: 8px;
}

.setting-label {
  display: block;
  margin-bottom: 4px;
  font-weight: 500;
}

.setting-item input[type="text"] {
  width: 100%;
}

.violations-list {
  margin-top: 15px;
}

.violations-list h5 {
  margin-bottom: 10px;
  color: #555;
}

.violations-container {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #ccc;
  border-radius: 3px;
  padding: 10px;
  background-color: white;
}

.flex {
  display: flex;
  justify-content: flex-start;
}

.objects-list {
  margin-top: 15px;
}

.objects-list h5 {
  margin-bottom: 10px;
  color: #555;
}

.objects-filter {
  margin-bottom: 15px;
}

.search-container {
  position: relative;
  display: flex;
  align-items: center;
}

.search-input {
  flex-grow: 1;
  padding: 8px;
  padding-right: 70px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.filter-buttons {
  position: absolute;
  right: 5px;
  display: flex;
  gap: 2px;
}

.btn-square {
  width: 30px;
  height: 30px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
}

.btn-text {
  font-size: 14px;
  line-height: 1;
}

.selected-objects-summary {
  margin-left: 15px;
  border-radius: 4px;
  border: 1px solid #ccc;
  overflow-y: auto;
  max-height: 353px;
}

.selected-objects-summary h6 {
  margin-top: 0;
  margin-bottom: 0;
  color: #333;
  position: sticky;
  top: 0;
  background-color: #f0f8ff;
  padding: 10px;
}

.selected-object-tag {
  background-color: #e1f5fe;
  padding: 2px 6px;
  border-radius: 3px;
  margin-right: 2px;
  font-size: 12px;
}

.objects-container {
  height: 300px;
  width: 348px;
  overflow-y: auto;
  border: 1px solid #ccc;
  border-radius: 3px;
  padding: 10px;
  background-color: white;
}

.object-item {
  margin-bottom: 8px;
  text-align: initial;
}

.object-item label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 14px;
}

.object-item input[type="checkbox"] {
  margin-right: 8px;
}

.no-objects-message {
  text-align: center;
  color: #777;
  font-style: italic;
  padding: 10px;
}

.violation-item {
  margin-bottom: 8px;
  text-align: initial;
}

.violation-item label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 14px;
}

.violation-item input[type="checkbox"] {
  margin-right: 8px;
}

.violation-item .critical {
  color: #d32f2f;
  font-weight: 500;
}

.critical-badge {
  background-color: #d32f2f;
  color: white;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 11px;
  margin-left: 5px;
}

.btn-sm {
  padding: 4px 12px;
  font-size: 13px;
}

.violations-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.btn-active {
  background-color: #d32f2f;
  color: white;
  border-color: #d32f2f;
}

.no-channel-message {
  color: #777;
  font-style: italic;
  padding: 8px 0;
}
</style>
